import { FDRStatus, FieldModel, ProgramEngagementModel } from '@cibo/core'
import {
  PaginatedFieldsProvider,
  ReadOnlyPaginatedFieldsTable,
  fieldEnrollmentColumn,
  fieldGrowerColumn,
  fieldIdentifierColumn,
  fieldUpdatedAtColumn,
  useLandManagement,
  usePaginatedFields,
} from '@cibo/landmanager'
import { Loading, useAuth, useWindowTitle } from '@cibo/profile'
import { useProgram, useProgramEngagements } from '@cibo/programs'
import { ListCard, Markdown } from '@cibo/ui'
import { Box, Button, Stack } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-pro'
import { EngagementImpossible } from 'components/EngagementImpossible'
import { PROGRAM_ROUTES } from 'navigation/types/routes'
import { useFieldTableActionsColumn } from 'pages/LandManager/useFieldTableActionsColumn'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, generatePath, useParams } from 'react-router-dom'
import {
  ENGAGEMENT_PATHS,
  ENGAGEMENT_ROUTES,
} from '../../../../../navigation/types/engagementRoutes'
import { FinishEngagementCTA } from '../FinishEngagementCTA'

export const EnrolledFields = () => {
  const { t } = useTranslation('impact/pages/ProgramV2Detail/EnrolledFields')
  const { t: tColumns } = useTranslation('@cibo/landmanager/FieldsTable')
  const { persona } = useAuth()
  const { programId = '' } = useParams()
  const program = useProgram({ programId })
  useWindowTitle(program.data?.content.fields.title)
  const { openFieldMetaDialog } = useLandManagement()
  const engagements = useProgramEngagements({ programId: [programId] })
  const firstEngagment =
    engagements.data?.items[0] && new ProgramEngagementModel(engagements.data?.items[0])
  const actionsColumn = useFieldTableActionsColumn({
    onEditMeta: resourceId => openFieldMetaDialog({ resourceId: resourceId as string }),
    reviewInformationPath: (fieldModel: FieldModel) => {
      if (!fieldModel.workflowForProgram(programId) || !firstEngagment) {
        return undefined
      }
      const { step, stage } = firstEngagment.lastStepAndStage()
      if (!stage || !step) {
        return undefined
      }
      return generatePath(`${PROGRAM_ROUTES.ENGAGEMENTS}/${ENGAGEMENT_ROUTES.STEP_STATUS}`, {
        programId,
        engagementId: fieldModel.workflowForProgram(programId) || '',
        stageId: stage.id,
        stepId: step.id,
      })
    },
    programId,
    hideFinishEnrollment: true,
  })
  const baseQuery = {
    fdrStatusAllOf: [{ programId, status: FDRStatus.enrolled }],
  }
  const enrolledFields = usePaginatedFields(0, 1, baseQuery)
  const availableForWorkflow = usePaginatedFields(0, 1, { availableForWorkflow: { programId } })

  const columns = useMemo(() => {
    return [
      actionsColumn,
      fieldIdentifierColumn({ t: tColumns, flex: 3 }),
      persona === 'grower' ? undefined : fieldGrowerColumn({ t: tColumns, width: 150 }),
      fieldEnrollmentColumn({ t: tColumns, programId, width: 150 }),

      fieldUpdatedAtColumn({ t: tColumns, width: 150, flex: 0 }),
    ].filter(c => !!c) as GridColDef[]
  }, [programId])
  const isFuture = program.data?.enrollmentWindow.enrollmentStatus === 'future'

  if (isFuture) {
    return <Navigate to={generatePath(PROGRAM_ROUTES.ABOUT, { programId })} replace />
  }

  if (enrolledFields.isLoading || availableForWorkflow.isLoading) return <Loading />

  if (!enrolledFields.data?.numAvailable) {
    if (!!engagements.data?.items?.[0]) {
      return (
        <FinishEngagementCTA
          markdown={t('finishEngagementCTAMd', {
            count: engagements.data.items[0].fieldCounts?.total,
          })}
          // @ts-ignore program is loaded
          engageType={program.data?.engageType}
          to={generatePath(`${PROGRAM_ROUTES.ENGAGEMENTS}/${ENGAGEMENT_PATHS.ENGAGEMENT}`, {
            programId,
            engagementId: engagements.data.items[0].id,
          })}
        />
      )
    }
    if (
      program.data?.enrollmentWindow.enrollmentStatus !== 'closed' &&
      !!program.data?.opportunitiesByWfStatus?.notInWorkflow[0].numResources
    ) {
      return (
        <Stack>
          <Box maxWidth={500}>
            <Markdown color="text.secondary">
              {t('eligibleFieldsCTAMd', {
                count: program.data.opportunitiesByWfStatus?.notInWorkflow[0].numResources,
              })}
            </Markdown>
          </Box>
          <Box>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              component={Link}
              to={generatePath(PROGRAM_ROUTES.FIELDS_ELIGIBLE, { programId })}
            >
              {t('seeEligible')}
            </Button>
          </Box>
        </Stack>
      )
    }
    return program.data?.enrollmentWindow.enrollmentStatus === 'closed' ? (
      <Navigate to={generatePath(PROGRAM_ROUTES.ABOUT, { programId })} replace />
    ) : (
      <EngagementImpossible programId={programId} />
    )
  }

  return (
    <ListCard title={t('programs')} subTitle={t('enrolledFields')}>
      <PaginatedFieldsProvider>
        <ReadOnlyPaginatedFieldsTable
          getRowHeight={() => 'auto'}
          columns={columns}
          baseQuery={baseQuery}
        />
      </PaginatedFieldsProvider>
    </ListCard>
  )
}
